import { useState } from "react";
import { observer } from "mobx-react";
import { useStore } from "../../../app/data/root.store";
import { VaultPendingFilesModalBody } from "./VaultPendingFilesModal.styles";
import { useVaultsFetchByStatus } from "../../hooks/useVaultsFetchByStatus";
import { confirmVaults, updateVaultsStatus } from "../../data/vault.service";
import { VaultPendingFilesBox } from "./VaultPendingFilesBox";
import { BaseModal } from "../../../../common/components/BaseModal/BaseModal";
import { VaultModalHeader } from "../VaultModalHeader/VaultModalHeader";
import { VaultPendingFilesActions } from "./VaultPendingFilesActions";
import { UploadingModalFooter } from "./UploadingModalFooter";
import { VaultDeleteModal } from "../VaultDeleteModal/VaultDeleteModal";
import { successNotification } from "../../../layout/components/Notifications/notifications";

const stopFetching = (vaults) => vaults.every(vault => vault.llmStatus !== 'in process');

export const VaultPendingFilesModal = observer(({ createItemsState = false }) => {
    const { vaultStore, userStore: { isWelcome }, metadataStore } = useStore();
    const [assetsHandling, setAssetsHandling] = useState({ createNewAssets: false, updateExistingAssets: false });
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const { vaults, isReady } = useVaultsFetchByStatus({
        status: 'Pending',
        stopFetching
    });

    const deleteVaults = async () => {
        setIsDeleting(true);
        await vaultStore.deleteVaults(vaults.map(vault => vault.id));
        vaultStore.setVaultFormStep(null);
        setIsDeleting(false);
    };

    const onClose = () => setShowDeleteModal(true);

    const onSubmit = async () => {
        setIsLoading(true);

        if (vaultStore.assetId) {
            await confirmVaults(vaults.map(vault => vault.id), false, false, [vaultStore.assetId]);
            vaultStore.setFilesData({ numOfItems: vaults.length, singleFileName: vaults[0].originalName });
            vaultStore.setVaultFormStep('deep-analysis');
            await vaultStore.refetchFiles();
            await metadataStore.fetchData();
            return;
        }

        const isAllFailed = vaults.every(vault => (('failed' === vault.llmStatus) || ('timeout' === vault.llmStatus) || ('rejected' === vault.llmStatus)));
        if (isAllFailed) {
            vaultStore.setFilesData({ numOfItems: vaults.length, singleFileName: vaults[0].originalName });
            const forceConfirm = !(assetsHandling.createNewAssets || assetsHandling.updateExistingAssets);
            const sendUploadConfirmationEmail = !forceConfirm;
            await confirmVaults(vaults.map(vault => vault.id), sendUploadConfirmationEmail, forceConfirm);

            await vaultStore.resetAndRefetchFiles();
            await metadataStore.fetchData();
            setIsLoading(false);

            if (forceConfirm) {
                vaultStore.setVaultFormStep(null);
                successNotification('Files were uploaded successfully.');
            } else {
                vaultStore.setVaultFormStep('deep-analysis');
            }
            return;
        }

        if (assetsHandling.createNewAssets || isWelcome || createItemsState) {
            await updateVaultsStatus(vaults.map(vault => vault.id), 'Pre-Process');
            await vaultStore.fetchVaultsFormStep();
            setIsLoading(false);
        } else if (assetsHandling.updateExistingAssets) {
            await updateVaultsStatus(vaults.map(vault => vault.id), 'Processing');
            await confirmVaults(vaults.map(vault => vault.id), true);
            
            await vaultStore.resetAndRefetchFiles();
            await metadataStore.fetchData();
            setIsLoading(false);

            vaultStore.setFilesData({ numOfItems: vaults.length, singleFileName: vaults[0].originalName });
            vaultStore.setVaultFormStep('deep-analysis');
        } else {
            await confirmVaults(vaults.map(vault => vault.id), false, true);
            await vaultStore.resetAndRefetchFiles();
            await metadataStore.fetchData();
            setIsLoading(false);

            vaultStore.setVaultFormStep(null);
            successNotification('Files were uploaded successfully.');
        }
    };

    const deleteVault = async (id) => {
        setIsDeleting(true);
        await vaultStore.deleteVaults([id]);
        const index = vaults.findIndex(vault => vault.id === id);
        vaults.splice(index, 1);
        successNotification('The file has been successfully removed.');
        if (vaults.length === 0) {
            vaultStore.setVaultFormStep(null);
        }
        setIsDeleting(false);
    };

    return (
        <BaseModal
            borderRadius={12}
            customModalWrapperStyles={{
                boxShadow: "0px 0px 2px 0px rgba(0, 0, 0, 0.30), 0px 4px 15px 0px rgba(0, 0, 0, 0.15)",
                flexShrink: 0,
                background: "#FFF",
                maxHeight: "calc(max(880px, 65vh))",
                height: "calc(100% - 48px)",
                position: 'relative'
            }}
            noSidebarMargin={isWelcome}
        >
            <VaultModalHeader onClose={onClose} assetId={vaultStore.assetId} createItemsState={createItemsState} />
            <VaultPendingFilesModalBody>
                <VaultPendingFilesBox filesPending={vaults} onDelete={deleteVault} />
                <VaultPendingFilesActions
                    filesPending={vaults}
                    assetsHandling={assetsHandling}
                    setAssetsHandling={setAssetsHandling}
                    areFilesReady={isReady}
                    showOptions={!vaultStore.assetId && !isWelcome && !createItemsState}
                />
            </VaultPendingFilesModalBody>
            <UploadingModalFooter
                onSubmit={onSubmit}
                isDeleting={isDeleting}
                isButtonDisabled={!isReady}
                isLoading={isLoading}
            />
            {showDeleteModal && (
                <VaultDeleteModal
                    onClose={() => setShowDeleteModal(false)}
                    onDelete={deleteVaults}
                />
            )}
        </BaseModal>
    );
});